import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BriefcaseIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { navigateToCustomerSearchResult } from '../components/GlobalSearch';

const SearchResults = () => {
  const { makeAuthenticatedRequest } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [groupedResults, setGroupedResults] = useState({});

  const performSearch = useCallback(async (searchQuery) => {
    const trimmed = (searchQuery || '').trim();
    if (trimmed.length < 2) {
      setResults([]);
      setGroupedResults({});
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await makeAuthenticatedRequest(
        `/global-search?q=${encodeURIComponent(trimmed)}`
      );
      setResults(data?.success ? (data.data || []) : []);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest]);

  useEffect(() => {
    const urlQuery = searchParams.get('q') || '';
    setQuery(urlQuery);
  }, [searchParams]);

  useEffect(() => {
    performSearch(searchParams.get('q') || '');
    // Search from the URL so the first Enter from the header is not lost
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const grouped = {};
    results.forEach(result => {
      if (!grouped[result.type]) {
        grouped[result.type] = [];
      }
      grouped[result.type].push(result);
    });
    setGroupedResults(grouped);
  }, [results]);

  const handleResultClick = (result) => {
    navigateToCustomerSearchResult(navigate, result);
  };

  const commitQuery = (nextQuery) => {
    const trimmed = nextQuery.trim();
    if (trimmed) {
      setSearchParams({ q: trimmed });
    } else {
      setSearchParams({});
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'job':
        return <BriefcaseIcon className="h-5 w-5 text-blue-600" />;
      case 'claimant':
        return <UserIcon className="h-5 w-5 text-green-600" />;
      case 'claim':
        return <DocumentTextIcon className="h-5 w-5 text-yellow-600" />;
      default:
        return <MagnifyingGlassIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      job: 'Jobs',
      claimant: 'Claimants',
      claim: 'Claims'
    };
    return labels[type] || type;
  };

  const typeOrder = ['claim', 'job', 'claimant'];

  return (
    <div className="min-h-full bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Search Results</h1>

          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitQuery(query);
                }
              }}
              placeholder="Search jobs, claimants, claim numbers, phones..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />
            {loading && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
              </div>
            )}
          </div>
        </div>

        {query && (
          <div className="mb-4 text-sm text-gray-600">
            {loading ? (
              'Searching...'
            ) : results.length > 0 ? (
              `Found ${results.length} result${results.length !== 1 ? 's' : ''} for "${query}"`
            ) : (
              `No results found for "${query}"`
            )}
          </div>
        )}

        {!loading && results.length > 0 && (
          <div className="space-y-6">
            {typeOrder.map(type => {
              if (!groupedResults[type] || groupedResults[type].length === 0) return null;

              return (
                <div key={type} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center space-x-2">
                      {getTypeIcon(type)}
                      <h2 className="text-lg font-semibold text-gray-900">
                        {getTypeLabel(type)} ({groupedResults[type].length})
                      </h2>
                    </div>
                  </div>

                  <div className="divide-y divide-gray-200">
                    {groupedResults[type].map((result, index) => (
                      <div
                        key={`${result.type}-${result.id}-${index}`}
                        onClick={() => handleResultClick(result)}
                        className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 mt-1">
                            {getTypeIcon(result.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {result.title}
                              </p>
                              <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded flex-shrink-0">
                                {result.type.replace('_', ' ')}
                              </span>
                            </div>
                            {result.subtitle && (
                              <p className="text-sm text-gray-500 truncate mb-1">
                                {result.subtitle}
                              </p>
                            )}
                            {result.metadata && (
                              <p className="text-xs text-gray-400 truncate">
                                {result.metadata}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && query && results.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <MagnifyingGlassIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
            <p className="text-sm text-gray-500">
              Try adjusting your search terms or check for typos.
            </p>
          </div>
        )}

        {!query && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <MagnifyingGlassIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Enter a search query</h3>
            <p className="text-sm text-gray-500">
              Search for jobs, claimants, and claim numbers on your billing account.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResults;
